/**
 * The tier ladder, sized to fit ONE screen.
 *
 * Owner's call on the offer pages — *„te poziomy są poucinane, nie mogą takie
 * być, wszystko musi być dobrze widoczne bez scrollowania"*. Measured before
 * anything was changed: on a 1000px viewport the head of `/pl/miniaturki` ran
 * to 331px and a tier card to 705px, so the ladder asked for 1076px of a
 * screen that had 1000. Every reader met the same thing — three cards with
 * their buttons under the fold.
 *
 * ## Why this is CSS and not a measurement
 *
 * The obvious fix is to measure the leftover space and scale the card into it.
 * That needs JavaScript, it needs a resize observer, and it renders the wrong
 * size once on every load before the observer fires. A card that is laid out
 * in viewport units simply arrives correct — including on the server, and
 * including in the skeleton that stands in for it.
 *
 * So every vertical measurement on the card that CAN give way is a `clamp()`
 * against `svh`: full size on a tall screen, tighter on a short one, and it
 * never crosses the floor underneath it. The floors are not decoration — the
 * row type is held at `0.75rem` because that is the site's stated legibility
 * floor (see `MICRO`), and no amount of leftover space is worth going under it.
 *
 * ## What gives way, and in which order
 *
 * The white space goes first ({@link TIER_FIT_VARS} `--tier-step`, `--tier-row-gap`),
 * then the card's padding, then the price figure. The feature rows themselves
 * never shrink: they are the thing the reader came to read, and thirteen of
 * them are the reason the card is tall in the first place.
 *
 * Nothing is hidden. A card that fits by dropping four rows into a "+4 more"
 * line fits by not answering the question, and the ask was the opposite one.
 */

/**
 * The offer as one screen: page head at the top, ladder in the space that is
 * left.
 *
 * `100svh` — the SMALL viewport height — rather than `100vh` or `100dvh`. On a
 * phone `vh` is the height with the browser's toolbars retracted, so a section
 * sized in `vh` is taller than the screen it is on until the reader scrolls;
 * `dvh` is correct at any moment but changes as the toolbars move, which
 * resizes every clamp below and makes the card breathe while the page is being
 * scrolled. `svh` is the one that holds still.
 *
 * `min-h`, not `h`: a ladder that genuinely cannot fit — four levels, a long
 * matrix, a 600px window — grows past the screen and the page scrolls, which is
 * the graceful end of this. Clipping it would put the cards back under a fold,
 * only this time with no way to reach them.
 */
export const OFFER_SCREEN = 'flex min-h-[100svh] flex-col'

/**
 * The card's vertical metrics, as the variables {@link TIER_FIT_VARS} defines
 * and the card reads.
 *
 * Set on the CARD, not on the page, so a tier card is correct wherever it is
 * rendered and no page can forget to hand it its sizes.
 *
 * - `--tier-pad` — the card's own padding. 24px on a tall screen, 16 at worst.
 * - `--tier-step` — the gap between the card's blocks (price, blurb, list,
 *   button). This was `mt-5` six times over, and six twenty-pixel gaps is
 *   120px of the 705 the card was spending.
 * - `--tier-tick` — the small gap: a caption under its figure, a rule under its
 *   label.
 * - `--tier-row-gap` — between two feature rows. Thirteen rows means twelve of
 *   these, so 8px → 4px is 48px of card, more than any other single change
 *   here.
 * - `--tier-price` — the price figure. Capped by the card's width (`cqw`, as
 *   before — four digits still have to sit on one line) AND now by the screen's
 *   height, because a 100px number is the second-biggest thing on the card.
 *   Its height term is the one thing here with a slope rather than a plain
 *   ratio (`9.8svh - 1.6rem`): the number is what the card is FOR, so it is the
 *   last thing that should be spending its size on a laptop and the first that
 *   should take the room back on a monitor. Full 6.4rem from ~1300px up, ~76px
 *   at 1038, ~55px at 820.
 *
 * Each one reaches its old, full value somewhere around a 1250px-tall window
 * and holds there. That is the calibration, and it is deliberate: the sizes
 * this card was drawn at are the sizes it keeps on a monitor with room for
 * them — the clamps only pay out on the screens that were cutting it off.
 */
export const TIER_FIT_VARS = [
  '[--tier-pad:clamp(1rem,1.9svh,1.5rem)]',
  '[--tier-step:clamp(0.6rem,1.5svh,1.25rem)]',
  '[--tier-tick:clamp(0.35rem,0.8svh,0.75rem)]',
  '[--tier-row-gap:clamp(0.25rem,0.5svh,0.5rem)]',
  '[--tier-price:min(24cqw,clamp(2.4rem,9.8svh_-_1.6rem,6.4rem))]',
].join(' ')

/**
 * What a tier card comes out at, for the skeleton that stands in for it.
 *
 * The same arithmetic as the card, expressed once: the head and the button are
 * near enough constant, the rows are a fixed thirteen, and everything between
 * them is the clamps above. A placeholder sized `h-[36rem]` against a card that
 * renders 705px is a 130px jump the moment the data lands — which is the exact
 * failure a skeleton exists to prevent.
 */
export const TIER_CARD_H = 'h-[30rem] md:h-[clamp(30rem,64svh,42rem)]'
